import alert from '@/plugins/alert'
import {
  RetestMarkSheet,
  RetestMark,
  FinalMarkSheet,
  MarkElement,
  CourseMark,
  Class,
  Attendance,
  Slot,
  Log,
  Mark,
  CourseMarkElement,
  LearningResult
} from '@/plugins/api'
import _ from 'lodash'
import { CourseMarkConfig } from '../../plugins/api.js'

export default {
  namespaced: true,
  state: {
    markElements: [],
    classData: {},
    slots: {
      // id : {slot}
    },
    attendances: {
      // [slotId + userId] : {attendance}
    },
    logs: [],
    students: {},
    marks: {},
    learningResults: [],
    markSheets: [],
    isLoadingMarkSheets: false,
    isLoadingRetestMarkSheets: false,
    isLoadingRetestMarks: false,
    isInitData: false,
    courseMarkElements: [],
    courseMarkConfigData: {},
    retestMarkSheets: [],
    retestMarks: []
  },
  actions: {
    async createMarkSheet({ commit }, { data }) {
      try {
        const res = await FinalMarkSheet.create(data)
        commit('addMarkSheet', res)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async createRetestMarkSheet({ commit }, { data }) {
      try {
        const res = await RetestMarkSheet.create(data)
        commit('addRetestMarkSheet', res)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async createRetestMark({ commit }, { data }) {
      try {
        const res = await RetestMark.create(data)
        commit('addRetestMark', res)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async initClass({ commit }, { id }) {
      if (!id) {
        return
      }
      try {
        commit('setIsInitData', true)
        const [classData, slots, attendances, marks, learningResults, retestMarks] =
          await Promise.all([
            Class.fetchOne(id),
            Slot.fetch({ class: id, _sort: 'startTime:asc' }),
            Attendance.fetch({ class: id, _limit: -1 }),
            Mark.fetch({ class: id, _limit: -1 }),
            LearningResult.fetch({ class: id }),
            RetestMark.fetch({ class: id, _limit: -1 })
          ])
        const courseMarkConfigId = _.get(classData, 'course_mark_config.id')
        const courseMarkConfigData = !courseMarkConfigId
          ? undefined
          : await CourseMarkConfig.fetchOne(courseMarkConfigId)
        commit('changeState', {
          classData,
          attendances: _.keyBy(attendances, a => a.slot.id + a.userId),
          slots: _.keyBy(slots, 'id'),
          students: _.keyBy(classData.students, 'id'),
          // marks: _.keyBy(marks, m => _.get(m, 'student.id', ''))
          marks,
          learningResults,
          courseMarkConfigData,
          retestMarks
        })
      } catch (error) {
        console.error(error)
        throw error
      } finally {
        commit('setIsInitData', false)
      }
    },
    async saveLearningResults({ commit }, { data }) {
      const res = await LearningResult.upsertMany({ data })
      return res
    },
    async removeLearningResults({ state, commit }, { data }) {
      state.learningResults = state.learningResults.filter(i => !data.includes(i.id))
      const res = await Promise.all(data.map(id => LearningResult.remove(id)))
      return res
    },
    async fetchSlots({ commit }, params) {
      commit('setSlots', await Slot.fetch({ ...params, _limit: -1, _sort: 'startTime:asc' }))
    },
    async fetchMarks({ commit }, params) {
      commit('setMarks', await Mark.fetch({ ...params, _limit: -1 }))
    },
    async fetchLearningResults({ commit }, params) {
      commit('setLearningResults', await LearningResult.fetch({ ...params, _limit: -1 }))
    },
    async fetchClass({ commit }, id) {
      // commit('setClass', null)
      commit('setClass', await Class.fetchOne(id))
    },
    async fetchLogs({ commit }, params) {
      commit('setLogs', await Log.fetch(params))
    },
    async fetchAttendances({ commit }, params) {
      commit('setAttendances', await Attendance.fetch({ ...params, _limit: -1 }))
    },
    async updateClass({ commit, state }, { id, optimisticUpdate, ...classData }) {
      if (optimisticUpdate) {
        commit('setClass', { ...state.classData, ...classData })
      }
      // const prevClass = state.classData

      const newCourseMarkConfigId =
        classData.course_mark_config === null
          ? null
          : (classData.course_mark_config || {}).id || classData.course_mark_config
      try {
        const [res, courseMarkConfigData] = await Promise.all([
          Class.update(id, classData),
          ...(newCourseMarkConfigId ? [CourseMarkConfig.fetchOne(newCourseMarkConfigId)] : [])
        ])
        if (!optimisticUpdate) {
          commit('setClass', { ...state.classData, ...res })
        }
        if (newCourseMarkConfigId === null) {
          commit('setCourseMarkConfigData', {})
        } else if (courseMarkConfigData) {
          commit('setCourseMarkConfigData', courseMarkConfigData)
        }
      } catch (error) {
        // commit('setClass', prevClass)
        console.error(error)
        throw error
      }
    },
    async updateLearningResult({ commit, state }, data) {
      const prevData = state.learningResults.find(i => i.id === data.id)

      commit('setLearningResult', { ...prevData, ...data })
      try {
        const { id, ...o } = data

        await LearningResult.update(id, o)
      } catch (error) {
        commit('setLearningResult', prevData)
        console.error(error)
        throw error
      }
    },
    async createAttendance({ commit }, { data, option = {} }) {
      commit('setAttendance', await Attendance.create(data, option))
    },
    async createAttendances({ dispatch }, attendances) {
      await Promise.all(attendances.map(item => dispatch('createAttendance', { data: item })))
    },
    async createSlots({ dispatch }, slots) {
      await Promise.all(slots.map(item => dispatch('createSlot', { data: item })))
    },
    async createSlot({ commit }, { data, options }) {
      try {
        commit('setSlot', await Slot.create(data, options))
      } catch (e) {
        alert.error(e)
      }
    },
    async createMarks({ dispatch }, marks) {
      await Promise.all(marks.map(item => dispatch('createMark', { data: item })))
    },
    async createMark({ commit }, { data, options }) {
      commit('setMark', await Mark.create(data, options))
    },
    async updateSlot({ commit }, { id, ...slot }) {
      try {
        commit('setSlot', await Slot.update(id, slot))
        alert.success('Cập nhật thành công!')
      } catch (e) {
        alert.error(e)
      }
    },
    async updateSlots({ dispatch }, items) {
      await Promise.all(items.map(item => dispatch('updateSlot', item)))
    },
    async removeSlot({ commit }, id) {
      commit('removeSlot', await Slot.remove(id))
    },
    setSlot({ commit, state }, slot) {
      commit('setSlot', slot)
    },
    async removeSlots({ dispatch }, items) {
      await Promise.all(items.map(item => dispatch('removeSlot', item.id)))
    },
    async updateMarks({ commit }, { data, id }) {
      commit('upsertManyMarks', (await Class.updateMarks(id, { data })).filter(Boolean))
    },
    clearMarkAndLearningResultData({ commit, state }, id) {
      state.marks = []
      state.learningResults = []
      return Class.clearMarkData(id)
    },
    async fetchMarkSheets({ commit }, classId) {
      try {
        commit('setIsLoadingMarkSheets', true)
        const markSheets = await FinalMarkSheet.fetch({ class: classId })
        commit('setMarkSheets', markSheets)
      } catch (e) {
        console.error(e)
        throw e
      } finally {
        commit('setIsLoadingMarkSheets', false)
      }
    },
    async fetchMarkSheet({ commit }, sheetId) {
      try {
        const markSheet = await FinalMarkSheet.fetchOne(sheetId)
        commit('setMarkSheet', markSheet)
      } catch (e) {
        console.error(e)
        throw e
      } finally {
        commit('setIsLoadingMarkSheets', false)
      }
    },
    async fetchCourseMarkElements({ commit }, courseMarkConfigId) {
      try {
        const res = await CourseMarkElement.fetch({ course_mark_config: courseMarkConfigId })
        commit('setCourseMarkElements', res)
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    async fetchMarkElements({ commit }, classId) {
      try {
        const markElements = await MarkElement.fetch({ class: classId, _limit: -1 })
        commit('setMarkElements', markElements)
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    async fetchRetestMarkSheets({ commit }, classId) {
      try {
        commit('setIsLoadingRetestMarkSheets', true)
        const retestMarkSheets = await RetestMarkSheet.fetch({ class: classId, _limit: -1 })
        commit('setRetestMarkSheets', retestMarkSheets)
      } catch (e) {
        console.error(e)
        throw e
      } finally {
        commit('setIsLoadingRetestMarkSheets', false)
      }
    },
    async fetchRetestMarks({ commit }, classId) {
      try {
        commit('setIsLoadingRetestMarks', true)
        const retestMarks = await RetestMark.fetch({ class: classId, _limit: -1 })
        commit('setRetestMarks', retestMarks)
      } catch (e) {
        console.error(e)
        throw e
      } finally {
        commit('setIsLoadingRetestMarks', false)
      }
    },
    async updateMarkSheet({ commit, state }, { id, ...payload }) {
      try {
        const res = await FinalMarkSheet.update(id, payload)
        commit('setMarkSheet', res)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async updateRetestMark({ commit, state }, { id, ...payload }) {
      try {
        const res = await RetestMark.update(id, payload)
        commit('setRetestMark', res)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async updateRetestMarkSheet({ commit, state }, { id, ...payload }) {
      try {
        const res = await RetestMarkSheet.update(id, payload)
        commit('setRetestMarkSheet', res)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async deleteMarkSheet({ commit, state }, sheetId) {
      try {
        await FinalMarkSheet.remove(sheetId)
        commit('removeSheet', sheetId)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async deleteRetestMarkSheet({ commit, state }, sheetId) {
      try {
        await RetestMarkSheet.remove(sheetId)
        commit('removeRetestMarkSheet', sheetId)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async deleteRetestMark({ commit, state }, markId) {
      try {
        commit('removeRetestMark', markId)
        await RetestMark.remove(markId)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async deleteAllMarkElementsOfSheet({ commit, state }, sheetId) {
      try {
        await FinalMarkSheet.removeMarkElements(sheetId)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async removeStudentMarkElements({ commit, state }, { studentId, sheetId }) {
      try {
        const jobs = [
          MarkElement.removeMany({ params: { student: studentId, final_mark_sheet: sheetId } })
        ]
        // const matchSheet = state.markSheets.find(i => i.id === sheetId)
        // if (matchSheet) {
        //   jobs.push(
        //     FinalMarkSheet.update(sheetId, {
        //       mark_elements: matchSheet.mark_elements.filter(i => i.student !== studentId)
        //     })
        //   )
        // }
        const [_, updatedSheet] = await Promise.all(jobs)
        commit('removeStudentMarkElements', studentId)
        // commit('setMarkSheet', updatedSheet)
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    async createManyMarkElements({ commit, state }, { sheetId, data }) {
      try {
        const res = await MarkElement.createMany(data)
        commit('addMarkElements', res)
        // const matchSheet = state.markSheets.find(i => i.id === sheetId)
        // if (!matchSheet) {
        //   return
        // }
        // const updatedSheet = await FinalMarkSheet.update(sheetId, {
        //   mark_elements: [...matchSheet.mark_elements, ...res.map(i => i.id)]
        // })
        // commit('setMarkSheet', updatedSheet)
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    async updateManyMarkElements({ commit, state }, { data }) {
      try {
        const res = await MarkElement.updateMany(data.map(i => ({ ...i, id: i.markElementId })))
        // if (!res.length) {
        //   return
        // }
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    async updateManyRetestMarks({ commit, state }, { data }) {
      try {
        const res = await RetestMark.updateMany(data)
        // if (!res.length) {
        //   return
        // }
        commit('updateManyRetestMarks', res)
      } catch (e) {
        console.error(e)
        throw e
      }
    }
  },
  mutations: {
    removeStudentMarkElements(state, studentId) {
      state.markElements = state.markElements.filter(i => i.student.id !== studentId)
    },
    addMarkSheet(state, ...markSheets) {
      state.markSheets.push(...markSheets)
    },
    addRetestMarkSheet(state, ...markSheets) {
      state.retestMarkSheets.push(...markSheets)
    },
    addRetestMark(state, ...marks) {
      state.retestMarks.push(...marks)
    },
    removeSheet(state, sheetId) {
      state.markSheets = state.markSheets.filter(i => i.id !== sheetId)
    },
    removeRetestMarkSheet(state, sheetId) {
      state.retestMarkSheets = state.retestMarkSheets.filter(i => i.id !== sheetId)
      state.retestMarks = state.retestMarks.filter(i => i.retest_mark_sheet.id !== sheetId)
    },
    removeRetestMark(state, markId) {
      state.retestMarks = state.retestMarks.filter(i => i.id !== markId)
    },
    // addMarkElements(state, ...markElements) {
    //   state.markElements.push(...markElements)
    // },
    // updateMarkElements(state, ...markElements) {
    //   state.markElements = _.unionBy([markElements, state.markElements], i => i.id)
    // },
    setClass(state, classData) {
      state.classData = classData
      state.students = _.keyBy(classData.students, 'id')
    },
    setCourseMarkConfigData(state, data) {
      state.courseMarkConfigData = data
    },
    setCourseMarkElements(state, data) {
      state.courseMarkElements = data
    },
    setRetestMarkSheets(state, data) {
      state.retestMarkSheets = data
    },
    setRetestMarks(state, data) {
      state.retestMarks = data
    },
    setAttendances(state, attendances) {
      state.attendances = attendances.reduce(
        (acc, cur) => ({
          ...acc,
          [cur.slot.id + cur.userId]: cur
        }),
        {}
      )
    },
    setAttendance(state, attendance) {
      state.attendances = {
        ...state.attendances,
        [attendance.slot.id + attendance.userId]: attendance
      }
    },
    setMark(state, mark) {
      state.marks = {
        ...state.marks,
        [mark.student.id]: mark || {}
      }
    },
    setLearningResults(state, learningResults) {
      state.learningResults = learningResults
    },
    setLearningResult(state, data) {
      state.learningResults = state.learningResults.map(i => (i.id === data.id ? data : i))
    },
    setMarks(state, marks) {
      state.marks = marks
    },
    upsertManyMarks(state, marks) {
      state.marks = state.marks
        .map(m => {
          const match = marks.find(nm => nm.id === m.id)
          if (match && match.value !== m.value) {
            return match
          }
          return m
        })
        .filter(i => i.value !== null)
    },
    setLogs(state, logs) {
      state.logs = logs
    },
    setSlot(state, slot) {
      state.slots = {
        ...state.slots,
        [slot.id]: slot
      }
    },
    setSlots(state, slots) {
      state.slots = {
        ...slots.reduce(
          (accumulator, currentValue) => ({
            ...accumulator,
            [currentValue.id]: currentValue
          }),
          {}
        )
      }
    },
    removeSlot(state, slot) {
      delete state.slots[slot.id]
      state.slots = { ...state.slots }
    },
    setMarkSheets(state, markSheets) {
      state.markSheets = markSheets
    },
    setMarkSheet(state, markSheet) {
      let match = false
      state.markSheets = state.markSheets.map(i => {
        match = match || i.id === markSheet.id
        return i.id === markSheet.id ? markSheet : i
      })
      if (!match) {
        state.markSheets.push(markSheet)
      }
    },
    setRetestMarkSheet(state, markSheet) {
      let match = false
      state.retestMarkSheets = state.retestMarkSheets.map(i => {
        match = match || i.id === markSheet.id
        return i.id === markSheet.id ? markSheet : i
      })
      if (!match) {
        state.retestMarkSheets.push(markSheet)
      }
    },
    setRetestMark(state, mark) {
      let match = false
      state.retestMarks = state.retestMarks.map(i => {
        match = match || i.id === mark.id
        return i.id === mark.id ? mark : i
      })
      if (!match) {
        state.retestMarks.push(mark)
      }
    },
    setIsLoadingMarkSheets(state, val) {
      state.isLoadingMarkSheets = val
    },
    setIsLoadingRetestMarks(state, val) {
      state.isLoadingRetestMarks = val
    },
    setIsLoadingRetestMarkSheets(state, val) {
      state.isLoadingRetestMarkSheets = val
    },
    setIsInitData(state, val) {
      state.isInitData = val
    },
    setMarkElements(state, markElements) {
      state.markElements = markElements
    },
    updateManyRetestMarks(state, marks) {
      const shouldPush = _.differenceBy(marks, state.retestMarks, i => i.id)
      state.retestMarks = state.retestMarks.map(i => {
        const match = marks.find(j => j.id === i.id)
        if (match) {
          return match
        }
        return i
      })
      state.retestMarks.push(...shouldPush)
    }
  },
  getters: {
    finalCourseMark(state) {
      return (state.courseMarkConfigData.course_marks || []).find(m => m.type === 'final')
    },
    classData: state => {
      if (_.isEmpty(state.classData)) {
        return state.classData
      }
      const output = { ...state.classData }
      output.course_mark_config = state.courseMarkConfigData

      return output
    },
    logs: state => {
      return state.logs
    },
    slots: state => {
      return Object.values(state.slots)
    },
    attendances: state => {
      return state.attendances
    },
    students: state => {
      return Object.values(state.students)
    }
  }
}
